import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Table2,
  FileText,
  GitCompare,
  SlidersHorizontal,
  Map,
  Upload,
  BookOpen,
  Zap,
  Banknote,
  Users as UsersIcon,
  LogOut,
  CalendarDays,
  Thermometer,
  Bus,
  LayoutGrid,
  Info,
  X,
} from "lucide-react";
import { useData } from "../lib/data-context";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/lotes", icon: Package, label: "Lotes" },
  { to: "/tabla", icon: Table2, label: "Tabla de resultados" },
  { to: "/comparador", icon: GitCompare, label: "Comparador" },
  { to: "/escenarios", icon: SlidersHorizontal, label: "Escenarios" },
  { to: "/inversion", icon: Banknote, label: "Plan de inversión" },
  { to: "/mapa", icon: Map, label: "Mapa" },
  { to: "/calendarios", icon: CalendarDays, label: "Calendarios y GTFS" },
  { to: "/planificacion", icon: Bus, label: "Planificación servicio" },
  { to: "/planificacion-flota", icon: LayoutGrid, label: "Cuadro horarios flota" },
  { to: "/climatologia", icon: Thermometer, label: "Climatología" },
  { to: "/carga", icon: Upload, label: "Carga de datos" },
  { to: "/metodologia", icon: BookOpen, label: "Metodología" },
];

export default function Layout({ children }) {
  const { origenDatos } = useData() || {};
  const { user, logout } = useAuth();
  const isSimulado = origenDatos === "simulado" || origenDatos === "mixto";
  const [versionInfo, setVersionInfo] = useState({ version: "v1.1", release_date: "", changelog: "" });
  const [showChangelog, setShowChangelog] = useState(false);

  useEffect(() => {
    api.get("/version").then((r) => setVersionInfo(r.data)).catch(() => {});
  }, []);

  const navItems = [...NAV];
  if (user?.role === "admin") {
    navItems.push({ to: "/usuarios", icon: UsersIcon, label: "Usuarios" });
  }

  return (
    <div className="min-h-screen flex bg-[#F5F6F8]">
      {/* Sidebar */}
      <aside
        className="w-64 fixed h-full bg-white border-r border-gray-200 z-40 flex flex-col"
        data-testid="sidebar"
      >
        <div className="px-5 pt-6 pb-5 border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#14532D 0%,#16A34A 100%)" }}>
              <Zap size={20} strokeWidth={2.5} className="text-white" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-[18px] leading-tight text-gray-900 tracking-tight">
                VOLT<span className="text-green-700">.IA</span>
              </div>
              <div className="text-[9px] font-semibold uppercase tracking-widest text-gray-500 mt-0.5">
                Movilidad eléctrica sostenible
              </div>
            </div>
          </div>
          <div className="mt-5">
            <div className="text-[10px] font-bold tracking-widest text-gray-400 uppercase">
              Estudio
            </div>
            <div className="text-xs text-gray-600 mt-1 leading-relaxed">
              Plataforma de análisis de viabilidad eléctrica para flotas de autobuses
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <div className="px-3 pb-2 text-[10px] font-bold tracking-widest text-gray-400 uppercase">
            Navegación
          </div>
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                "sidebar-link" + (isActive ? " active" : "")
              }
              data-testid={`nav-${label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <Icon size={17} strokeWidth={1.75} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-5 py-4 border-t border-gray-200">
          {user && (
            <div className="mb-3 pb-3 border-b border-gray-100">
              <div className="text-[10px] font-bold tracking-widest text-gray-400 uppercase mb-1">
                Sesión
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-gray-900 truncate" data-testid="current-user">
                    {user.nombre || user.username}
                  </div>
                  <div className="text-[10px] text-gray-500 truncate">
                    {user.role === "admin" ? "Administrador" : (user.concesiones || []).join(" · ") || "Sin concesiones"}
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-900"
                  title="Cerrar sesión"
                  data-testid="btn-logout"
                >
                  <LogOut size={15} />
                </button>
              </div>
            </div>
          )}
          <div className="text-[10px] font-bold tracking-widest text-gray-400 uppercase mb-1">
            Versión
          </div>
          <button
            onClick={() => setShowChangelog(true)}
            className="text-xs text-gray-600 hover:text-emerald-700 transition-colors inline-flex items-center gap-1"
            data-testid="btn-version"
            title="Ver historial de cambios"
          >
            VOLT.IA · <span className="font-mono font-bold">{versionInfo.version}</span>
            <Info size={11} className="opacity-60" />
          </button>
          {versionInfo.release_date && (
            <div className="text-[10px] text-gray-400 mt-0.5 font-mono">
              {versionInfo.release_date}
            </div>
          )}
          <div className="text-[11px] text-gray-400 mt-1">
            Movilidad sostenible
          </div>
        </div>
      </aside>

      {/* Modal changelog */}
      {showChangelog && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowChangelog(false)}
          data-testid="changelog-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-lg text-gray-900">Historial de cambios</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Versión actual <strong className="text-emerald-700 font-mono">{versionInfo.version}</strong> · {versionInfo.release_date}
                </p>
              </div>
              <button
                onClick={() => setShowChangelog(false)}
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
                data-testid="btn-close-changelog"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
              {versionInfo.changelog || "No hay changelog disponible."}
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="ml-64 flex-1 flex flex-col min-h-screen">
        <div
          className={"crtm-banner " + (isSimulado ? "simulado" : "")}
          data-testid="banner-origen-datos"
        >
          {origenDatos === "anteproyecto" && (
            <span>Dataset cargado desde anteproyecto · datos reales por línea</span>
          )}
          {origenDatos === "simulado" && (
            <span>⚠ Datos 100% simulados · uso técnico/demostrativo</span>
          )}
          {origenDatos === "mixto" && (
            <span>
              Datos mixtos · parte real (anteproyecto) + parte simulada
            </span>
          )}
          {origenDatos === "cargado" && (
            <span>Datos cargados por el usuario</span>
          )}
        </div>

        <main className="flex-1 p-8" data-testid="main-content">
          {children}
        </main>

        <footer className="px-8 py-5 border-t border-gray-200 bg-white text-xs text-gray-500 flex justify-between">
          <span>VOLT.IA · Movilidad eléctrica sostenible</span>
          <span>
            Los resultados son estimaciones técnicas sujetas a validación con
            datos reales de operación.
          </span>
        </footer>
      </div>
    </div>
  );
}
